import { isAxiosError } from "axios";
import axios, { getData } from "../../../axios";
import { createSlice } from "@reduxjs/toolkit";
import * as tPromise from "io-ts-promise";
import { documentsT, documentsByStopIdT, Document } from "./types";
import { createErrorReportingAsyncThunk } from "../../helpers";

const prefix = "/data/stop-documents";

type State = {
  byStopId: Record<string, Document[] | undefined>;
  // Stops whose documents are being fetched, so a stop list remounting
  // meanwhile doesn't fire the same requests again.
  loadingStopIds: Record<string, true | undefined>;
};

const initialState: State = {
  byStopId: {},
  loadingStopIds: {},
};

type StateWithStopDocuments = { data: { stopDocuments: State } };

type LoadDocumentsArg = {
  stopId: string;
  silent?: boolean;
  force?: boolean;
};

export const loadDocuments = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  // `silent` is for stops loaded from a redux-cached form draft (e.g. a
  // client cargo template picked earlier, then edited elsewhere): the stop
  // may no longer exist server-side, and that 404 is expected there, not a
  // bug to report — treat it as "no documents" instead of an error.
  // `force` reloads even if a load is already in flight (after an upload or
  // a delete, the in-flight answer may be stale).
  async ({ stopId, silent }: LoadDocumentsArg): Promise<Document[]> => {
    try {
      return await axios
        .get(`/api/stops/${stopId}/documents`)
        .then(getData)
        .then(tPromise.decode(documentsT));
    } catch (e) {
      if (silent && isAxiosError(e) && e.response?.status === 404) {
        return [];
      }
      throw e;
    }
  },
  {
    condition: (
      { stopId, force }: LoadDocumentsArg,
      { getState }: { getState: () => unknown }
    ) =>
      force ||
      !(getState() as StateWithStopDocuments).data.stopDocuments
        .loadingStopIds[stopId],
  }
);

// All of a cargo's stop documents in one request (and one store update),
// instead of one per stop. `stopIds` are the stops the caller is about to
// show, marked as loading right away.
export const loadDocumentsForCargo = createErrorReportingAsyncThunk(
  `${prefix}/load-for-cargo`,
  async ({
    cargoId,
  }: {
    cargoId: string;
    stopIds: string[];
  }): Promise<Record<string, Document[]>> =>
    axios
      .get(`/api/cargos/${cargoId}/stop-documents`)
      .then(getData)
      .then(tPromise.decode(documentsByStopIdT))
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadDocuments.pending, (state, action) => {
        state.loadingStopIds[action.meta.arg.stopId] = true;
      })
      .addCase(loadDocuments.fulfilled, (state, action) => {
        const { stopId } = action.meta.arg;
        state.byStopId[stopId] = action.payload;
        delete state.loadingStopIds[stopId];
      })
      .addCase(loadDocuments.rejected, (state, action) => {
        delete state.loadingStopIds[action.meta.arg.stopId];
      })
      .addCase(loadDocumentsForCargo.pending, (state, action) => {
        for (const stopId of action.meta.arg.stopIds) {
          state.loadingStopIds[stopId] = true;
        }
      })
      .addCase(loadDocumentsForCargo.fulfilled, (state, action) => {
        Object.assign(state.byStopId, action.payload);
        for (const stopId of action.meta.arg.stopIds) {
          delete state.loadingStopIds[stopId];
        }
      })
      .addCase(loadDocumentsForCargo.rejected, (state, action) => {
        for (const stopId of action.meta.arg.stopIds) {
          delete state.loadingStopIds[stopId];
        }
      });
  },
});

export default slice.reducer;
